import { defaultRegionForCountry, type MarketConfig, type TenantRegion } from '@etiquetachef/types';
import { CONTINENT_KEYS, type ContinentKey } from './worldCountries';
import { ALL_PLAY_LOCATIONS, PLAY_LOCATIONS_BY_CONTINENT } from './playStoreCountries';

/**
 * Uma linha da tabela: um local da lista da Play (ou um país com doc fora
 * dela). `market` nulo = país sem doc em `/config/markets/items` — linha
 * "Não configurado", que existe só na tela (#957).
 */
export type CountryRow = {
  country: string;
  market: MarketConfig | null;
  continent: ContinentKey | null;
  /**
   * #1078 (F2 da #960): região em que os tenants NOVOS do país nascem quando a chave
   * `config/regions` está ligada — o override do mercado ou o padrão do grupo
   * (`defaultRegionForCountry`, a mesma função do servidor).
   */
  region: TenantRegion;
  /** `override` = gravada em `config/markets/items/{CC}.region`; `default` = padrão do grupo. */
  regionSource: 'override' | 'default';
};

const CONTINENT_BY_CODE: ReadonlyMap<string, ContinentKey> = new Map(
  CONTINENT_KEYS.flatMap((k) => PLAY_LOCATIONS_BY_CONTINENT[k].map((c) => [c, k] as const)),
);

/**
 * Monta as linhas da aba Países: os 225 locais da Play + todo país com doc,
 * mesmo fora da lista (nenhum doc gravado some da tela).
 *
 * Origem: #957 — 2026-10-13 11:25 +0200.
 *
 * Contrato: puro; uma linha por código, sem duplicata; `market` é o doc
 * quando existe; `region`/`regionSource` (#1078) vêm do override do doc ou do
 * padrão do grupo. Ordem: a de entrada (a tabela ordena depois).
 *
 * Usado por: `CountriesTab.tsx` (aba Países de `/countries`, admin web) e
 * `CountriesTab.test.tsx`.
 *
 * Histórico:
 *  - 2026-10-29 10:20 +0100 — (#1078, F2 da #960) `region` e `regionSource`.
 */
export function buildCountryRows(markets: readonly MarketConfig[]): CountryRow[] {
  const byCode = new Map(markets.map((m) => [m.country, m]));
  const codes = [...new Set([...ALL_PLAY_LOCATIONS, ...byCode.keys()])];
  return codes.map((country) => {
    const market = byCode.get(country) ?? null;
    const override = market?.region;
    return {
      country,
      market,
      continent: CONTINENT_BY_CODE.get(country) ?? null,
      region: override ?? defaultRegionForCountry(country),
      regionSource: override !== undefined ? 'override' : 'default',
    };
  });
}

/** `active` ausente no doc = ativo (contrato do `MarketConfigSchema`). */
export function isActive(row: CountryRow): boolean {
  return row.market !== null && (row.market.active ?? true);
}
